import type { Flow } from '@aj/coach';
import type { StudentPath } from '@aj/core';

import type { useT } from '../providers/I18nProvider.js';
import type { MessageKey } from '@aj/i18n';

/**
 * What the coach teaches, and when.
 *
 * Onboarding runs on the LIVE Home rather than on a tour of mock screens.
 * That is a deliberate choice about how people here learn an app: being shown
 * on the real thing transfers, because the row you practised on is the row
 * you tick tomorrow. A dedicated onboarding screen teaches a screen you never
 * see again.
 *
 * The coach never writes anything. Each step points at a real control and
 * waits for the app to report that the real thing happened — so validation,
 * error states and the write path are all the ordinary ones, and this file
 * stays a script rather than a second implementation of the app.
 *
 * Anchors are `data-coach` attributes. Grep for one to find what is taught.
 */
/**
 * What the coach must know that this package cannot compute.
 *
 * The flows are rebuilt whenever this changes, which is how a step gets copy
 * about a specific habit without `@aj/coach` ever learning what a habit is.
 */
export type CoachContext = {
  /** The habit whose plan can now be proposed, and when it usually happens. */
  plan?: { habitId: string; name: string; time: string };
  /** The habit missed yesterday that has no floor. */
  floor?: { habitId: string; name: string };
};

/**
 * Anchors offered when confirming a plan.
 *
 * Prayer times and school do the work: they already happen at the same point
 * every day without anyone having to remember them, which is the entire job
 * of an anchor. A clock is not an anchor — it can be missed by forty minutes,
 * and then the plan is simply wrong (§53.1).
 */
/** Offered in the onboarding question. Order is offer order, and the exam
 *  years come first because that is who this app is for. */
const PATHS: StudentPath[] = ['hsc', 'ssc', 'admission', 'university', 'other'];

const ANCHORS: MessageKey[] = [
  'anchor.fajr',
  'anchor.school',
  'anchor.asr',
  'anchor.maghrib',
  'anchor.dinner',
];

export function coachFlows(t: ReturnType<typeof useT>, ctx: CoachContext = {}): Flow[] {
  return [
    {
      id: 'onboarding',
      priority: 'onboarding',
      steps: [
        {
          kind: 'modal',
          title: t('coach.welcome.title'),
          body: t('coach.welcome.body'),
          advance: 'next',
        },
        /**
         * The only thing onboarding asks before it starts teaching.
         *
         * One tap, and it earns the interruption by paying twice: it picks
         * the register the whole app speaks in, and it is the key the goal
         * preset packs are filed under — so answering "HSC" is what puts a
         * chapter list one tap away in the very next step. A question that
         * only set an adjective would not be worth asking.
         *
         * Optional, because a flow that cannot be escaped is a trap. Skipping
         * costs nothing: `flavourOf` falls back to the category of whatever
         * goal they set, which is better evidence anyway.
         */
        {
          kind: 'modal',
          title: t('coach.path.title'),
          body: t('coach.path.body'),
          input: {
            // Object chips: the host gets back a stable id rather than having
            // to match translated copy back to a meaning.
            chips: PATHS.map((path) => ({ label: t(`path.${path}`), value: path })),
            optional: true,
          },
        },
        {
          kind: 'spotlight',
          // Tapping the button REPLACES it with the composer, so the step
          // follows: ring the button, then ring the form it became. Pointing
          // only at `set-goal` left the spotlight on the coordinates of an
          // element that no longer existed, dimming the composer it had just
          // opened. Composer first — when both somehow exist, the newer thing
          // is the one being taught.
          anchor: ['goal-compose', 'set-goal'],
          title: t('coach.goal.title'),
          body: t('coach.goal.body'),
          // Satisfied by a goal existing, not by agreement.
          advance: { on: 'goal.started' },
        },
        {
          kind: 'spotlight',
          anchor: 'add-habit',
          body: t('coach.addHabit.body'),
          // No Next button: the step is done when a habit exists, not when
          // the student agrees that habits are a good idea.
          advance: { on: 'habit.created' },
        },
        {
          kind: 'spotlight',
          anchor: 'habit-row',
          title: t('coach.promise.title'),
          body: t('coach.promise.body'),
          advance: 'next',
        },
        {
          kind: 'spotlight',
          anchor: 'now-action',
          title: t('coach.now.title'),
          body: t('coach.now.body'),
          advance: 'next',
        },
      ],
    },

    /**
     * The obstacle, asked after the first missed day.
     *
     * Not at minute one. "What usually gets in your way?" before anything has
     * happened returns "laziness", which is worth nothing to the weekly
     * review. Asked the morning after a real miss it is recall rather than
     * introspection, and the answer is specific enough to plan around.
     */
    {
      id: 'obstacle',
      priority: 'ambient',
      steps: [
        {
          kind: 'modal',
          title: t('coach.obstacle.title'),
          body: t('coach.obstacle.body'),
          input: {
            chips: [
              t('coach.obstacle.phone'),
              t('coach.obstacle.tired'),
              t('coach.obstacle.busy'),
              t('coach.obstacle.unclear'),
            ],
            allowFreeText: true,
            placeholder: t('coach.obstacle.placeholder'),
            optional: true,
          },
        },
      ],
    },


    /**
     * The if-then plan, PROPOSED rather than asked for.
     *
     * Fires once a habit has been done enough times to have a rhythm — day
     * two or three, typically. Deliberately NOT after a miss: the effect is
     * partly prospective, working by being rehearsed before the moment, so
     * waiting for a failure gives up more of it than waiting two days does.
     *
     * The body carries the hour the app has actually observed, so the student
     * is confirming a description of what they already do rather than
     * composing a plan for a habit they have barely begun.
     */
    {
      id: 'plan',
      priority: 'ambient',
      steps: [
        {
          kind: 'modal',
          title: t('coach.plan.title'),
          body: ctx.plan
            ? t('coach.plan.body', { name: ctx.plan.name, time: ctx.plan.time })
            : '',
          input: { chips: ANCHORS.map((key) => t(key)), optional: true },
        },
      ],
    },

    /**
     * The floor, asked the morning after a miss.
     *
     * The floor exists to survive a bad day, so a bad day is the first moment
     * the question means anything. Asked at creation it is a solution to a
     * problem the student has not had yet, and one more field between them
     * and a working app.
     */
    {
      id: 'floor',
      priority: 'ambient',
      steps: [
        {
          kind: 'modal',
          title: t('coach.floor.title'),
          body: ctx.floor ? t('coach.floor.body', { name: ctx.floor.name }) : '',
          input: {
            allowFreeText: true,
            placeholder: t('coach.floor.placeholder'),
            optional: true,
          },
        },
      ],
    },
    /**
     * The kept-rate bar, explained the day it starts existing.
     *
     * It is hidden until there are seven days of history, because a bar at 0%
     * on day two is a verdict on nobody — so the first time a student sees it
     * is the first time it can be explained.
     */
    {
      id: 'kept-rate',
      priority: 'ambient',
      steps: [
        {
          kind: 'spotlight',
          anchor: 'kept-rate',
          title: t('coach.keptRate.title'),
          body: t('coach.keptRate.body'),
          advance: 'next',
        },
      ],
    },
  ];
}
